import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import sroApi from '@/lib/sro-api'

// ==================== Config List ====================

export function useSROConfigs() {
  return useQuery({
    queryKey: ['sro-configs'],
    queryFn: async () => {
      const res = await sroApi.getConfigs()
      return res.data
    },
    retry: false,
  })
}

export function useCreateSROConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string }) => sroApi.createConfig(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sro-configs'] }),
  })
}

export function useDeleteSROConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (configId: number) => sroApi.deleteConfig(configId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sro-configs'] }),
  })
}

// ==================== Config Detail ====================

export function useSROConfig(configId: number | undefined) {
  return useQuery({
    queryKey: ['sro-config', configId],
    queryFn: async () => {
      const res = await sroApi.getConfig(configId!)
      return res.data
    },
    enabled: !!configId,
    retry: false,
  })
}

export function useUpdateSROConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, data }: { configId: number; data: any }) => sroApi.updateConfig(configId, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['sro-config', vars.configId] })
      qc.invalidateQueries({ queryKey: ['sro-configs'] })
    },
  })
}

// ==================== Export / Import ====================

export function useExportSROConfig() {
  return useMutation({
    mutationFn: (configId: number) => sroApi.exportConfig(configId),
  })
}

export function useImportSROConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: any) => sroApi.importConfig(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sro-configs'] }),
  })
}

// ==================== Intents ====================

export function useSROIntents(configId: number | undefined) {
  return useQuery({
    queryKey: ['sro-intents', configId],
    queryFn: async () => {
      const res = await sroApi.getIntents(configId!)
      return res.data
    },
    enabled: !!configId,
  })
}

export function useCreateSROIntent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, data }: { configId: number; data: any }) => sroApi.createIntent(configId, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-intents', vars.configId] }),
  })
}

export function useUpdateSROIntent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, id, data }: { configId: number; id: number; data: any }) => sroApi.updateIntent(configId, id, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-intents', vars.configId] }),
  })
}

export function useDeleteSROIntent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, id }: { configId: number; id: number }) => sroApi.deleteIntent(configId, id),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-intents', vars.configId] }),
  })
}

// ==================== Departments ====================

export function useSRODepartments(configId: number | undefined) {
  return useQuery({
    queryKey: ['sro-departments', configId],
    queryFn: async () => {
      const res = await sroApi.getDepartments(configId!)
      return res.data
    },
    enabled: !!configId,
  })
}

export function useCreateSRODepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, data }: { configId: number; data: any }) => sroApi.createDepartment(configId, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-departments', vars.configId] }),
  })
}

export function useUpdateSRODepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, id, data }: { configId: number; id: number; data: any }) => sroApi.updateDepartment(configId, id, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-departments', vars.configId] }),
  })
}

export function useDeleteSRODepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, id }: { configId: number; id: number }) => sroApi.deleteDepartment(configId, id),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-departments', vars.configId] }),
  })
}

// ==================== Sub-Departments ====================

export function useCreateSROSubDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, deptId, data }: { configId: number; deptId: number; data: any }) =>
      sroApi.createSubDepartment(configId, deptId, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-departments', vars.configId] }),
  })
}

export function useUpdateSROSubDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, deptId, subId, data }: { configId: number; deptId: number; subId: number; data: any }) =>
      sroApi.updateSubDepartment(configId, deptId, subId, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-departments', vars.configId] }),
  })
}

export function useDeleteSROSubDepartment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, deptId, subId }: { configId: number; deptId: number; subId: number }) =>
      sroApi.deleteSubDepartment(configId, deptId, subId),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-departments', vars.configId] }),
  })
}

// ==================== Service Area ====================

export function useSROServiceArea(configId: number | undefined) {
  return useQuery({
    queryKey: ['sro-service-area', configId],
    queryFn: async () => {
      const res = await sroApi.getServiceArea(configId!)
      return res.data
    },
    enabled: !!configId,
    retry: false,
  })
}

export function useUpdateSROServiceArea() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ configId, data }: { configId: number; data: any }) => sroApi.updateServiceArea(configId, data),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['sro-service-area', vars.configId] }),
  })
}

// ==================== Sessions ====================

export function useSROSessions() {
  return useQuery({
    queryKey: ['sro-sessions'],
    queryFn: async () => {
      const res = await sroApi.getSessions()
      return res.data
    },
    refetchInterval: 10000,
  })
}

export function useSROSessionStats() {
  return useQuery({
    queryKey: ['sro-session-stats'],
    queryFn: async () => {
      const res = await sroApi.getSessionStats()
      return res.data
    },
    refetchInterval: 15000,
  })
}

export function useDeleteSROSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) => sroApi.deleteSession(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sro-sessions'] })
      qc.invalidateQueries({ queryKey: ['sro-session-stats'] })
    },
  })
}
